/* THE DOGS · Shared product-gallery gestures and unobstructed navigation. */
(() => {
  'use strict';
  if (window.TDGalleryUX) return;

  const ROOTS = '#td-rampa-editorial,#td-majestuoso-editorial,#funda-editorial,#td-nails,#td-cama,#td-elevada,#td-comedero,#td-asiento,#td-nido,#td-piel-editorial,#td-luxury-editorial,#td-impermeable-editorial';
  const STAGES = '.gallery-stage,.hero-media,.gallery-shell';
  const controllers = new Map();
  const liveImage = state => Array.from(state.live.querySelectorAll('img')).find(image => !image.closest('[hidden],.hidden'));
  const imageKey = image => ['src', 'srcset', 'sizes'].map(name => image.getAttribute(name) || '').join('|');
  const revealMedia = (state, item) => {
    state.cancelReveal?.();
    const epoch = ++state.mediaEpoch;
    const slide = state.slides[state.index];
    const preview = slide.querySelector('.td-native-preview');
    if (!preview) return;
    preview.hidden = false;
    const removers = [];
    let frame = 0, videoFrame = 0, decoding = false;
    const current = () => state.mediaEpoch === epoch && state.live.parentElement === slide && slide.isConnected;
    const listen = (node, type, handler) => {
      node.addEventListener(type, handler);
      removers.push(() => node.removeEventListener(type, handler));
    };
    const reveal = ready => {
      if (!current() || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (current() && (!ready || ready())) preview.hidden = true;
      });
    };
    const video = item.type === 'video' ? state.live.querySelector('video') : null;
    state.cancelReveal = () => {
      removers.forEach(remove => remove());
      if (frame) cancelAnimationFrame(frame);
      if (videoFrame && video?.cancelVideoFrameCallback) video.cancelVideoFrameCallback(videoFrame);
      state.mediaEpoch += 1;
    };
    if (preview.tagName !== 'IMG') { preview.hidden = true; return; }
    if (video) {
      const source = () => video.getAttribute('src') || video.querySelector('source')?.getAttribute('src') || video.dataset.src || '';
      let videoEpoch = 0;
      const showFrame = () => {
        if (!current() || video.paused || video.readyState < 2) return;
        const generation = videoEpoch;
        const candidate = video.currentSrc;
        const requestedSource = source();
        const ready = () => current() && generation === videoEpoch && video.readyState >= 2 && candidate === video.currentSrc && requestedSource === source();
        if (video.requestVideoFrameCallback) {
          if (!videoFrame) videoFrame = video.requestVideoFrameCallback(() => {
            if (!current() || generation !== videoEpoch) return;
            videoFrame = 0;
            if (ready()) reveal(ready);
          });
        } else reveal(ready);
      };
      ['playing', 'loadeddata', 'timeupdate'].forEach(type => listen(video, type, showFrame));
      listen(video, 'emptied', () => {
        videoEpoch += 1;
        if (frame) { cancelAnimationFrame(frame); frame = 0; }
        if (videoFrame && video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(videoFrame);
        videoFrame = 0;
        if (current()) preview.hidden = false;
      });
      if (source()) showFrame();
      return;
    }
    const image = liveImage(state);
    if (!image || item.type !== 'image') { preview.hidden = true; return; }
    const source = imageKey(image);
    const ready = () => {
      if (!current() || imageKey(image) !== source || !image.complete || !image.naturalWidth || decoding) return;
      decoding = true;
      const candidate = image.currentSrc;
      let decoded;
      try { decoded = image.decode ? image.decode() : Promise.resolve(); } catch (_) { decoded = Promise.reject(); }
      Promise.resolve(decoded).then(() => {
        decoding = false;
        if (!current() || imageKey(image) !== source || !image.complete || !image.naturalWidth) return;
        if (candidate !== image.currentSrc) { ready(); return; }
        reveal(() => imageKey(image) === source && image.complete && image.naturalWidth > 0 && candidate === image.currentSrc);
      }, () => { decoding = false; });
    };
    listen(image, 'load', ready);
    ready();
  };
  const sync = (root, options) => {
    const stage = root?.querySelector(STAGES);
    if (!stage || !options.items?.length) return;
    initialize(root);
    const controller = controllers.get(stage);
    if (!controller) return;
    if (!controller.native) {
      const live = document.createElement('div');
      live.className = 'td-native-live';
      live.dataset.tdGalleryStage = 'true';
      live.append(...stage.childNodes);
      const viewport = document.createElement('div');
      viewport.className = 'td-native-viewport';
      viewport.tabIndex = 0;
      viewport.setAttribute('role', 'group');
      viewport.setAttribute('aria-label', root.querySelector('.gallery')?.getAttribute('aria-label') || stage.getAttribute('aria-label') || 'Galería del producto');
      stage.append(viewport);
      stage.dataset.tdNativeGallery = 'true';
      const state = { live, viewport, slides: [], index: -1, signature: '', options, timer: 0, scrolling: false, pointer: null, touches: 0, suppressClick: false, mediaEpoch: 0, imageSizes: '' };
      controller.native = state;
      const settle = () => {
        clearTimeout(state.timer);
        if (state.pointer || state.touches || !viewport.clientWidth) return;
        const index = Math.max(0, Math.min(state.slides.length - 1, Math.round(viewport.scrollLeft / viewport.clientWidth)));
        state.scrolling = false;
        if (index !== state.index) state.options.select(index);
        stage.dispatchEvent(new CustomEvent('td:gallery-media-change', { bubbles: true }));
      };
      viewport.addEventListener('scroll', () => {
        state.scrolling = true;
        state.suppressClick = true;
        clearTimeout(state.timer);
        state.timer = setTimeout(settle, 140);
        stage.dispatchEvent(new CustomEvent('td:gallery-media-change', { bubbles: true }));
      }, { passive: true });
      viewport.addEventListener('scrollend', settle);
      viewport.addEventListener('pointerdown', event => {
        state.suppressClick = false;
        if (event.pointerType === 'mouse') return;
        state.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, left: viewport.scrollLeft };
      }, { passive: true });
      viewport.addEventListener('pointermove', event => {
        if (state.pointer?.id === event.pointerId && Math.hypot(event.clientX - state.pointer.x, event.clientY - state.pointer.y) > 8) state.suppressClick = true;
      }, { passive: true });
      const release = event => {
        if (state.pointer?.id !== event.pointerId) return;
        state.pointer = null;
        clearTimeout(state.timer);
        state.timer = setTimeout(settle, 140);
      };
      viewport.addEventListener('pointerup', release, { passive: true });
      viewport.addEventListener('pointercancel', release, { passive: true });
      const touchState = event => {
        state.touches = event.touches.length;
        if (!state.touches) {
          state.pointer = null;
          clearTimeout(state.timer);
          state.timer = setTimeout(settle, 140);
        }
      };
      ['touchstart', 'touchend', 'touchcancel'].forEach(type => viewport.addEventListener(type, touchState, { passive: true }));
      viewport.addEventListener('click', event => {
        if (event.detail && state.suppressClick) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
      viewport.addEventListener('keydown', event => {
        if (event.target !== viewport || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? state.slides.length - 1 : Math.max(0, Math.min(state.slides.length - 1, state.index + (event.key === 'ArrowRight' ? 1 : -1)));
        if (index !== state.index) state.options.select(index);
      });
      if (window.ResizeObserver) {
        state.resize = new ResizeObserver(() => {
          if (!state.pointer && !state.touches && !state.scrolling) viewport.scrollTo({ left: state.slides[state.index]?.offsetLeft || 0, behavior: 'instant' });
        });
        state.resize.observe(viewport);
      }
    }
    const state = controller.native;
    const signature = JSON.stringify(options.items.map(item => [item.type, item.src, item.srcset, item.poster, item.sizes]));
    const index = Math.max(0, Math.min(options.items.length - 1, Number(options.index) || 0));
    state.imageSizes = liveImage(state)?.getAttribute('sizes') || state.imageSizes || `${stage.clientWidth}px`;
    state.options = options;
    const rebuild = state.signature !== signature;
    if (rebuild) {
      state.live.remove();
      state.slides = options.items.map(item => {
        const slide = document.createElement('div');
        slide.className = 'td-native-slide';
        const source = item.type === 'video' ? item.poster : item.src;
        if (source) {
          const preview = document.createElement('img');
          preview.className = 'td-native-preview';
          if (item.type === 'video') preview.classList.add('td-native-video-preview');
          preview.sizes = item.sizes || state.imageSizes;
          if (item.type !== 'video' && item.srcset) preview.srcset = item.srcset;
          preview.src = source;
          preview.alt = '';
          preview.loading = 'lazy';
          preview.decoding = 'async';
          preview.draggable = false;
          slide.append(preview);
        } else {
          const preview = document.createElement('span');
          preview.className = 'td-native-preview td-native-placeholder';
          preview.textContent = item.caption || item.alt || '';
          slide.append(preview);
        }
        return slide;
      });
      state.viewport.replaceChildren(...state.slides);
      state.signature = signature;
    }
    state.index = index;
    state.slides.forEach((slide, position) => {
      const selected = position === index;
      slide.classList.toggle('is-active', selected);
      if (selected) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
      const preview = slide.querySelector('.td-native-preview');
      if (preview) {
        preview.hidden = false;
        if (preview.tagName === 'IMG') {
          if (options.items[position].type !== 'video') preview.sizes = options.items[position].sizes || state.imageSizes;
          const distance = Math.abs(position - index);
          if (distance <= 1 || distance === state.slides.length - 1) preview.loading = 'eager';
        } else preview.textContent = options.items[position].caption || options.items[position].alt || '';
      }
    });
    const selectedSlide = state.slides[index];
    if (state.live.parentElement !== selectedSlide) selectedSlide.append(state.live);
    revealMedia(state, options.items[index]);
    if (rebuild || Math.abs(state.viewport.scrollLeft - selectedSlide.offsetLeft) > 1) {
      state.scrolling = false;
      state.viewport.scrollTo({ left: selectedSlide.offsetLeft, behavior: 'instant' });
    }
  };
  const makeArrow = (direction, label) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${direction < 0 ? 'M20 12H4m6-6-6 6 6 6' : 'M4 12h16m-6-6 6 6-6 6'}" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    return button;
  };

  const initialize = root => {
    const stage = root.querySelector(STAGES);
    if (!stage || controllers.has(stage)) return;
    stage.dataset.tdGalleryStage = 'true';
    root.dataset.tdGalleryRoot = 'true';
    const previous = root.querySelector('#gallery-prev');
    const next = root.querySelector('#gallery-next');
    const controller = {
      root, stage,
      move(direction) {
        const button = direction > 0 ? next : previous;
        if (button) {
          if (button.disabled || button.getAttribute('aria-disabled') === 'true') return false;
          button.click();
        } else {
          // Nido uses its existing thumbnail selection as its source of truth.
          const thumbs = Array.from(root.querySelectorAll('[data-gallery-index]')).filter(item => !item.hidden && !item.disabled);
          if (thumbs.length < 2) return false;
          const current = Math.max(0, thumbs.findIndex(item => item.getAttribute('aria-pressed') === 'true' || item.classList.contains('is-active')));
          thumbs[(current + direction + thumbs.length) % thumbs.length].click();
        }
        stage.dispatchEvent(new CustomEvent('td:gallery-media-change', { bubbles: true }));
        return true;
      }
    };
    controllers.set(stage, controller);

    if ((previous && stage.contains(previous)) || (!previous && !next)) {
      const navigation = document.createElement('div');
      navigation.dataset.tdGalleryNav = 'true';
      navigation.setAttribute('role', 'group');
      navigation.setAttribute('aria-label', root.getAttribute('data-gallery-navigation-label') || 'Navegación de la galería');
      const counter = root.querySelector('.gallery-counter,#gallery-position,#gallery-counter')
        || root.querySelector('#gallery-current')?.parentElement;
      const prevButton = previous || makeArrow(-1, 'Ver imagen anterior');
      const nextButton = next || makeArrow(1, 'Ver imagen siguiente');
      if (!previous) prevButton.addEventListener('click', () => controller.move(-1));
      if (!next) nextButton.addEventListener('click', () => controller.move(1));
      navigation.append(prevButton);
      if (counter) navigation.append(counter);
      navigation.append(nextButton);
      stage.after(navigation);
      // Remove only empty decorative containers left by the moved controls.
      stage.querySelectorAll('.gallery-tools,.gallery-nav,.gallery-navigation').forEach(container => {
        if (!container.querySelector('button:not(.zoom-trigger),[id^="gallery-current"],[id^="gallery-counter"]')) container.hidden = true;
      });
    } else if (previous && next && previous.parentElement === next.parentElement) {
      previous.parentElement.dataset.tdGalleryNav = 'true';
    }

    // Keep the image itself as the zoom target; remove the separate expand icon.
    const existingImageButton = stage.querySelector('#image-open,#zoom-image');
    const zoomButton = root.querySelector('#gallery-zoom,.gallery-zoom,#zoom-button,.zoom-trigger');
    root.querySelectorAll('.image-expand').forEach(icon => { icon.dataset.tdZoomIcon = 'true'; });
    if (zoomButton && !existingImageButton && root.matches('#td-piel-editorial,#td-luxury-editorial,#td-impermeable-editorial')) {
      zoomButton.dataset.tdImageZoom = 'true';
      stage.append(zoomButton);
      const syncZoom = () => {
        const image = (stage.querySelector('.td-native-live') || stage).querySelector('img');
        const available = Boolean(image && !image.closest('[hidden],.hidden'));
        const value = available ? 'true' : 'false';
        if (zoomButton.dataset.tdZoomAvailable !== value) zoomButton.dataset.tdZoomAvailable = value;
      };
      const observer = new MutationObserver(syncZoom);
      observer.observe(stage, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class'] });
      controller.observer = observer;
      syncZoom();
    } else if (zoomButton) {
      zoomButton.dataset.tdZoomIcon = 'true';
    }
  };
  const scan = scope => {
    if (scope.matches?.(ROOTS)) initialize(scope);
    scope.querySelectorAll?.(ROOTS).forEach(initialize);
  };
  const cleanup = scope => {
    controllers.forEach((controller, stage) => {
      if (scope.contains(stage)) { controller.observer?.disconnect(); controller.native?.resize?.disconnect(); controller.native?.cancelReveal?.(); clearTimeout(controller.native?.timer); controllers.delete(stage); }
    });
  };
  window.TDGalleryUX = { scan, sync };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => cleanup(event.target));
})();

